"use client";

/**
 * Suppression de compte — page exigée par Google Play.
 *
 * Le centre juridique traite déjà le sujet, mais il n'offre qu'une voie :
 * « depuis l'application ». Or l'exigence de Play porte précisément sur le cas
 * inverse — quelqu'un qui a désinstallé l'app doit pouvoir demander la
 * suppression sans la réinstaller. D'où le second chemin, par courriel.
 *
 * L'adresse est donnée telle quelle dans la Play Console, champ « URL de
 * suppression du compte ». Elle doit rester stable.
 */

import React from "react";
import Link from "next/link";
import { Smartphone, Mail, Trash2, Archive } from "lucide-react";
import { useLocale } from "next-intl";

function Titre({
  icone: Icone,
  children,
}: {
  icone?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-center gap-3">
      {Icone && <Icone className="h-5 w-5 shrink-0 text-terracotta" aria-hidden={true} />}
      <h2 className="font-display text-2xl font-extrabold tracking-tight text-espresso">
        {children}
      </h2>
    </div>
  );
}

export default function SuppressionComptePage() {
  const fr = useLocale() !== "en";
  const T = (a: string, b: string) => (fr ? a : b);
  const locale = fr ? "fr" : "en";

  return (
    <>
      {/* En-tête */}
      <div className="border-b border-line bg-cream-soft py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-7">
          <div className="eyebrow">{T("Compte Expedys", "Expedys account")}</div>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tightest text-espresso md:text-5xl">
            {T("Supprimer votre compte et ", "Delete your account and ")}
            <span className="text-terracotta">{T("vos données", "your data")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink">
            {T(
              "Vous pouvez fermer votre compte Expedys à tout moment, depuis l'application ou en nous écrivant. Cette page explique comment faire, ce qui est effacé, et ce que la loi nous oblige à conserver.",
              "You can close your Expedys account at any time, from the app or by writing to us. This page explains how, what gets erased, and what the law requires us to keep.",
            )}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-7 py-14 md:py-20">
        <div className="space-y-12 leading-relaxed text-ink">
          {/* Depuis l'application */}
          <section>
            <Titre icone={Smartphone}>{T("Depuis l'application", "From the app")}</Titre>
            <p className="mb-4">
              {T(
                "C'est la voie la plus rapide : la suppression est immédiate, sans intervention de notre part.",
                "This is the fastest route: deletion is immediate, with no action needed on our side.",
              )}
            </p>
            <ol className="ml-5 list-decimal space-y-2 marker:font-semibold marker:text-terracotta">
              <li>{T("Ouvrez l'onglet Profil.", "Open the Profile tab.")}</li>
              <li>{T("Touchez Compte.", "Tap Account.")}</li>
              <li>{T("Faites défiler jusqu'à Supprimer mon compte.", "Scroll down to Delete my account.")}</li>
              <li>
                {T(
                  "Tapez SUPPRIMER dans le champ de confirmation, puis validez.",
                  "Type DELETE in the confirmation field, then confirm.",
                )}
              </li>
            </ol>
          </section>

          {/* Sans l'application */}
          <section className="rounded-card-lg border border-[#EBD3C6] bg-[#F4E5DD] p-6">
            <Titre icone={Mail}>{T("Sans l'application", "Without the app")}</Titre>
            <p className="mb-4">
              {T(
                "Si vous avez désinstallé Expedys, ou si vous préférez ne pas passer par l'application, écrivez-nous à l'adresse ci-dessous. Vous n'avez rien à réinstaller.",
                "If you have uninstalled Expedys, or would rather not go through the app, write to us at the address below. You do not need to reinstall anything.",
              )}
            </p>
            <p className="mb-4">
              <a
                href={`mailto:contact@expedys.com?subject=${encodeURIComponent(
                  T("Suppression de compte", "Account deletion"),
                )}`}
                className="font-semibold text-terracotta underline underline-offset-4"
              >
                contact@expedys.com
              </a>
            </p>
            <p className="mb-2 font-semibold text-espresso">
              {T("Indiquez dans votre message :", "Include in your message:")}
            </p>
            <ul className="ml-5 list-disc space-y-1">
              <li>
                {T(
                  "le numéro de téléphone associé à votre compte,",
                  "the phone number linked to your account,",
                )}
              </li>
              <li>{T("votre nom tel qu'il figure au compte.", "your name as it appears on the account.")}</li>
            </ul>
            <p className="mt-4">
              {T(
                "Nous vérifions votre identité avant toute suppression, puis traitons la demande sous 30 jours. En pratique, c'est bien plus court.",
                "We verify your identity before any deletion, then process the request within 30 days. In practice it is much faster.",
              )}
            </p>
          </section>

          {/* Ce qui est supprimé */}
          <section>
            <Titre icone={Trash2}>{T("Ce qui est effacé", "What is erased")}</Titre>
            <p className="mb-4">
              {T(
                "Dès la suppression confirmée, ces informations disparaissent de nos bases :",
                "As soon as deletion is confirmed, this information disappears from our databases:",
              )}
            </p>
            <ul className="ml-5 list-disc space-y-2">
              <li>{T("votre nom, votre numéro de téléphone et votre courriel,", "your name, phone number and email,")}</li>
              <li>{T("votre photo de profil, supprimée du stockage,", "your profile photo, removed from storage,")}</li>
              <li>{T("votre adresse de résidence,", "your home address,")}</li>
              <li>
                {T(
                  "le lien avec votre compte Apple ou Google, s'il y en avait un,",
                  "the link to your Apple or Google account, if there was one,",
                )}
              </li>
              <li>{T("les codes de vérification en cours,", "any pending verification codes,")}</li>
              <li>
                {T(
                  "les paiements commencés mais non aboutis, qui sont annulés.",
                  "payments started but not completed, which are cancelled.",
                )}
              </li>
            </ul>
            <p className="mt-4">
              {T(
                "Votre compte est désactivé au même moment : la connexion n'est plus possible, et votre numéro redevient disponible pour une nouvelle inscription.",
                "Your account is deactivated at the same time: you can no longer sign in, and your number becomes available again for a new registration.",
              )}
            </p>
          </section>

          {/* Ce qui est conservé */}
          <section className="border-l-4 border-terracotta py-2 pl-6">
            <Titre icone={Archive}>{T("Ce que nous devons conserver", "What we must keep")}</Titre>
            <p className="mb-4">
              {T(
                "L'historique de vos envois et de vos paiements reste dans nos systèmes. Nous n'avons pas le choix : le transport international de marchandises et l'encaissement de fonds nous imposent des obligations comptables, fiscales et de lutte contre le blanchiment.",
                "The history of your shipments and payments stays in our systems. We have no choice: international freight and handling funds impose accounting, tax and anti-money-laundering obligations on us.",
              )}
            </p>
            <p>
              {T(
                "Ces enregistrements ne portent plus votre identité. Ils subsistent rattachés à un compte vidé de toute donnée personnelle, et ne permettent plus de remonter jusqu'à vous.",
                "These records no longer carry your identity. They remain attached to an account stripped of all personal data, and can no longer be traced back to you.",
              )}
            </p>
          </section>

          {/* Questions */}
          <section className="border-t border-line pt-10">
            <Titre>{T("Une question avant de décider ?", "A question before you decide?")}</Titre>
            <p>
              {T(
                "Écrivez-nous à ",
                "Write to us at ",
              )}
              <a
                href="mailto:contact@expedys.com"
                className="font-semibold text-terracotta underline underline-offset-4"
              >
                contact@expedys.com
              </a>
              {T(" ou appelez le ", " or call ")}
              <a href="tel:+14385440767" className="font-semibold text-terracotta underline underline-offset-4">
                +1 438 544 0767
              </a>
              {T(
                ". Le détail du traitement de vos données figure dans notre ",
                ". Full details on how we handle your data are in our ",
              )}
              <Link
                href={`/${locale}/legal?tab=privacy`}
                className="font-semibold text-terracotta underline underline-offset-4"
              >
                {T("politique de confidentialité", "privacy policy")}
              </Link>
              .
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
